import React, { useMemo } from 'react'

import * as R from 'ramda'

import {
  Flex,
  NumericalSizingLiteral,
  StatusBadge,
  StatusBadgeVariantLiteral,
  Tooltip,
} from '@driftt/dds'
import {
  type KPICardStatusIndicatorVariantLiteral,
  KPICard,
} from '@driftt/dds.reports'

import { getScoreComponents } from 'utils/kpi'

import { Score } from './index'

interface IKPICard {
  title: string
  subtitle: string
  children?: React.ReactNode
  score: Score
  metric?: number | null
  metricRange: string
  isLoading?: boolean
  isError?: boolean
  hasNoData?: boolean
  formatMetric: (metric: number) => string
  childrenFlexGap?: NumericalSizingLiteral
}

export const ReportKPICard: React.FC<IKPICard> = ({
  title,
  subtitle,
  score,
  metric,
  metricRange,
  children,
  isLoading,
  isError,
  hasNoData,
  formatMetric,
  childrenFlexGap = 'half',
}) => {
  const { statusIndicator, badgeVariant, badgeText } = useMemo(
    () => getScoreComponents(score),
    [score]
  )

  return (
    <KPICard
      metric={metric}
      hasNoData={hasNoData}
      hasError={isError}
      isLoading={isLoading}
      statusIndicator={statusIndicator as KPICardStatusIndicatorVariantLiteral}
      formatMetric={formatMetric}
    >
      <KPICard.Header title={title} description={subtitle} />
      <KPICard.Content as={Flex} css={{ flexGrow: 1 }} direction="column">
        <Flex gap="half" direction="column" justifyContent="flex-start">
          {!R.isNil(badgeVariant) && (
            <Tooltip side="right" content={metricRange}>
              <StatusBadge variant={badgeVariant as StatusBadgeVariantLiteral}>
                {badgeText}
              </StatusBadge>
            </Tooltip>
          )}
        </Flex>
        <KPICard.Metric />
        {!!children && (
          <Flex
            gap={childrenFlexGap}
            direction="column"
            justifyContent="flex-end"
          >
            {children}
          </Flex>
        )}
      </KPICard.Content>
    </KPICard>
  )
}
